import { useState, useRef } from "react"
import type { Suggestion } from "~/react/components/email_accounts/types"
import { getErrorMessageFromResponse } from "~/react/components/email_accounts/utils"

interface UseDomainSuggestionsReturn {
  suggestions: Suggestion[]
  setSuggestions: React.Dispatch<React.SetStateAction<Suggestion[]>>
  suggestionsOffset: number
  setSuggestionsOffset: React.Dispatch<React.SetStateAction<number>>
  isGenerating: boolean
  setIsGenerating: React.Dispatch<React.SetStateAction<boolean>>
  errorTimeoutRef: React.MutableRefObject<number | null>
  generateSuggestions: (opts: { baseDomain: string; domainsCount: number; replace?: boolean }) => Promise<void>
  addManualDomain: (opts: { manualDomain: string; suggestions: Suggestion[]; selectedMap: Record<string, boolean>; setSelectedMap: React.Dispatch<React.SetStateAction<Record<string, boolean>>> }) => Promise<{ success: boolean; error?: string }>
}

export const useDomainSuggestions = (): UseDomainSuggestionsReturn => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [suggestionsOffset, setSuggestionsOffset] = useState<number>(0)
  const [isGenerating, setIsGenerating] = useState<boolean>(false)
  const errorTimeoutRef = useRef<number | null>(null)

  const generateSuggestions = async (opts: { baseDomain: string; domainsCount: number; replace?: boolean }) => {
    const { baseDomain, domainsCount, replace = false } = opts
    if (isGenerating) return
    setIsGenerating(true)
    const desired = domainsCount
    const placeholders: Suggestion[] = Array.from({ length: desired }).map(() => ({
      id: `placeholder-${crypto.randomUUID()}`,
      status: "loading",
    }))

    const currentOffset = replace ? 0 : suggestionsOffset
    setSuggestions((prev) => (replace ? [...placeholders] : [...prev, ...placeholders]))

    try {
      const requestCount = desired
      const response = await fetch("/mailpool/domain_suggestions", {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
        },
        body: JSON.stringify({ base_domain: baseDomain, count: requestCount, offset: currentOffset }),
      })
      if (!response.ok) throw new Error("network-error")
      const data: { suggestions: Array<{ domain: string; price_cents: number }>; empty?: boolean } =
        await response.json()

      // Show flash alert if response is empty
      if (data.empty || (data.suggestions || []).length === 0) {
        const App = (window as any).App
        if (App && typeof App.displayFlash === "function") {
          App.displayFlash("warning", i18n.t("js.email_accounts.provisioning.no_suggestions_available"))
        }
      }

      setSuggestions((prev) => {
        const next = [...prev]
        const placeholderIds = new Set(placeholders.map((p) => p.id))
        const indices = next
          .map((s, idx) => ({ s, idx }))
          .filter(({ s }) => s.status === "loading" && placeholderIds.has(s.id))
          .map(({ idx }) => idx)

        const existingDomains = new Set(
          next
            .filter((s) => s.status === "ready" && !placeholderIds.has(s.id))
            .map((s) => s.domain?.toLowerCase())
            .filter(Boolean),
        )

        const items = (data.suggestions || []).filter(
          (item) =>           !existingDomains.has(item.domain?.toLowerCase()),
        )

        indices.forEach((idx, i) => {
          const item = items[i]
          if (item) {
            next[idx] = {
              id: next[idx].id,
              status: "ready",
              domain: item.domain,
              priceCents: item.price_cents,
            }
          }
        })
        const filledCount = Math.min(items.length, indices.length)
        if (filledCount < indices.length) {
          const idsToRemove = new Set(indices.slice(filledCount).map((i) => next[i].id))
          return next.filter((s) => !(s.status === "loading" && idsToRemove.has(s.id)))
        }
        return next
      })
      if (!replace && data.suggestions && data.suggestions.length > 0) {
        setSuggestionsOffset((prev) => prev + data.suggestions.length)
      } else if (replace) {
        setSuggestionsOffset(data.suggestions?.length || 0)
      }
    } catch (e) {
      setSuggestions((prev) => prev.map((s) => (s.status === "loading" ? { ...s, status: "error" } : s)))
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current)
      }
      errorTimeoutRef.current = window.setTimeout(() => {
        setSuggestions((prev) => prev.filter((s) => s.status !== "error"))
        errorTimeoutRef.current = null
      }, 4000)
    } finally {
      setIsGenerating(false)
    }
  }

  const addManualDomain = async (opts: {
    manualDomain: string
    suggestions: Suggestion[]
    selectedMap: Record<string, boolean>
    setSelectedMap: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
  }): Promise<{ success: boolean; error?: string }> => {
    const { manualDomain, suggestions, setSelectedMap } = opts
    const normalized = manualDomain.trim().toLowerCase()
    const duplicate = suggestions.some(
      (s) => s.status === "ready" && (s.domain || "").toLowerCase() === normalized,
    )
    if (duplicate) {
      return { success: false, error: i18n.t("js.email_accounts.provisioning.domain_duplicate") }
    }

    try {
      const response = await fetch("/mailpool/check_domain", {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
        },
        body: JSON.stringify({ domain: normalized }),
      })

      if (!response.ok) {
        // Check for mailpool_error in the response
        const contentType = response.headers.get("content-type")
        if (contentType?.includes("application/json")) {
          try {
            const errorData = await response.json()
            if (errorData.error === "mailpool_error") {
              return { success: false, error: i18n.t("js.email_accounts.provisioning.mailpool_error") }
            }
          } catch {
            // If parsing fails, fall through to generic error
          }
        }
        throw new Error("network-error")

      }

      const data: { available: boolean; price_cents?: number; domain?: string } = await response.json()

      if (!data.available) {
        const domainLabel = (data.domain || normalized).toLowerCase()
        return {
          success: false,
          error: i18n.t("js.email_accounts.provisioning.domain_unavailable", { domain: domainLabel }),
        }
      } else {
        const id = `manual-${crypto.randomUUID()}`
        if (
          suggestions.some(
            (s) => s.status === "ready" && (s.domain || "").toLowerCase() === (data.domain || normalized).toLowerCase(),
          )
        ) {
          return { success: false, error: i18n.t("js.email_accounts.provisioning.domain_duplicate") }
        }
        setSuggestions((prev) => [
          ...prev,
          { id, status: "ready", domain: (data.domain || normalized).toLowerCase(), priceCents: data.price_cents },
        ])
        setSelectedMap((prev) => ({ ...prev, [id]: true }))
        return { success: true }
      }
    } catch (e) {
      return { success: false, error: i18n.t("js.email_accounts.provisioning.api_error") }
    }
  }

  return {
    suggestions,
    setSuggestions,
    suggestionsOffset,
    setSuggestionsOffset,
    isGenerating,
    setIsGenerating,
    errorTimeoutRef,
    generateSuggestions,
    addManualDomain,
  }
}

